import { useCallback, useMemo, useState } from 'react'

import { CompareSelectionApi } from './use-compare-selection-types'

const MAX_SELECTION = 2

/**
 * Manages a small selection set (max 2) for the compare-two-listings
 * feature. Toggling a third listing pushes the oldest selection out so
 * the renter can rapidly pivot between candidates.
 *
 * @returns {CompareSelectionApi} the selection state + setters
 */
const useCompareSelection = (): CompareSelectionApi => {
  const [selectedIds, setSelectedIds] = useState<string[]>([])

  const toggleSelected = useCallback((listingId: string): void => {
    setSelectedIds(previous => {
      if (previous.includes(listingId) === true) {
        return previous.filter(entry => entry !== listingId)
      }

      if (previous.length < MAX_SELECTION) {
        return [...previous, listingId]
      }

      return [previous[1], listingId]
    })
  }, [])

  const clearSelection = useCallback((): void => {
    setSelectedIds([])
  }, [])

  const isSelected = useCallback(
    (listingId: string): boolean => selectedIds.includes(listingId),
    [selectedIds],
  )

  const canCompare = selectedIds.length === MAX_SELECTION

  return useMemo(
    () => ({
      selectedIds,
      isSelected,
      toggleSelected,
      clearSelection,
      canCompare,
    }),
    [selectedIds, isSelected, toggleSelected, clearSelection, canCompare],
  )
}

export { useCompareSelection }
